import BGD2026 from "@assets/budget/B2026-logo.svg";
import GELogo from "@assets/ge/GE2025-logo.png";
import SG60Gif from "@assets/ndp/sg60.gif";
import Divider from "@elements/Divider/Divider";
import Image from "@elements/ResponsiveImage/Image";
import Heading from "@elements/Typography/Heading";
import type { ReactElement } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

// Constants
const NDP_2024_ICON_URL =
  "https://static1.tamilmurasu.com.sg/media/ndp-59-icon.png?w=120&dpr=1.0";

const SPECIAL_HEADER_CONFIG = {
  "தேசிய தினம் 2024": {
    type: "ndp-2024",
    image: NDP_2024_ICON_URL,
    imageSize: { width: 120, height: 120 },
  },
  "பட்ஜெட் 2025": {
    type: "budget-2025",
    image: undefined,
    imageSize: undefined,
  },
  "பட்ஜெட் 2026": {
    type: "budget-2026",
    image: BGD2026,
    imageSize: { width: 141, height: 48 },
  },
  "பொதுத் தேர்தல் 2025": {
    type: "election-2025",
    image: GELogo,
    imageSize: { width: 48, height: 48 },
  },
  "தேசிய தினம் 2025": {
    type: "ndp-2025",
    image: SG60Gif,
    imageSize: { width: 40, height: 40 },
  },
  "தீபாவளி 2025": {
    type: "deepavali-2025",
    image: undefined,
    imageSize: undefined,
  },
  தீபாவளி: {
    type: "deepavali",
    image: undefined,
    imageSize: undefined,
  },
} as const;

export type SpecialHeaderProps = {
  title: string;
};

export function SpecialHeader({ title }: SpecialHeaderProps): ReactElement {
  const config =
    SPECIAL_HEADER_CONFIG[title as keyof typeof SPECIAL_HEADER_CONFIG];

  const { type, image, imageSize } = config;

  const CustomHeading = ({ className }: { className?: string }) => {
    return (
      <>
        <Heading
          Element="h1"
          className={twMerge(
            "font-bold font-primary text-2.5md leading-960 lg:text-1xl",
            className,
          )}
        >
          {title}
        </Heading>
      </>
    );
  };

  return (
    <>
      {type === "ndp-2024" && (
        <div className="ndp-section-title bg-pink-200 lg:h-15 h-13 lg:mt-xm lg:mb-500 lg:mx-auto mt-200 mb-sm mx-[calc(-50vw+50%)]">
          <div className="inline-flex">
            <Image
              srcWidth={imageSize.width}
              srcHeight={imageSize.height}
              src={image}
              objectFit="contain"
              alt={title}
              className="lg:w-30 w-23 absolute lg:-mt-400 lg:ml-md -mt-300 ml-xs"
            />
            <div className="py-xxs pr-md lg:pl-900 pl-800">
              <Link reloadDocument to="/keywords/national-day-2024">
                <CustomHeading className="leading-700 mt-xxs bg-gradient-to-r from-white-100 to-yellow-100 bg-clip-text text-transparent" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {type === "budget-2025" && (
        <div>
          <CustomHeading className="text-blue-800 mb-5" />
        </div>
      )}

      {type === "budget-2026" && (
        <>
          <div className="flex items-center justify-between pb-2.5">
            <CustomHeading className="" />
            <Image
              srcWidth={imageSize.width}
              srcHeight={imageSize.height}
              src={image}
              objectFit="contain"
              alt={title}
            />
          </div>
          <Divider className="border-b border-gray-2200 mb-5" />
        </>
      )}

      {type === "election-2025" && (
        <>
          <div>
            <Image
              srcWidth={imageSize.width}
              srcHeight={imageSize.height}
              src={image}
              objectFit="contain"
              alt={title}
              className="lg:w-12 w-12 absolute"
            />
            <CustomHeading className="text-pink-200 mb-5 pl-12 ml-2.5" />
            <Divider className="border-b border-gray-2200 mb-5" />
          </div>
        </>
      )}

      {type === "ndp-2025" && (
        <>
          <div>
            <Image
              srcWidth={imageSize.width}
              srcHeight={imageSize.height}
              src={image}
              objectFit="contain"
              alt={title}
              className="lg:w-10 w-10 absolute"
            />
            <CustomHeading className="lg:text-lg text-red-450 pl-12 ml-2.5" />
          </div>
        </>
      )}

      {(type === "deepavali-2025" || type === "deepavali") && (
        <>
          <div>
            <CustomHeading className="text-purple-800 mb-5" />
          </div>
        </>
      )}
    </>
  );
}
